import { useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ajvResolver, ValidatorsProps } from '@ui/src';
import { useAppSelector } from '@store';
import { AuthNextAction, FormValues } from '../../types';
import {
  useLoginMutation,
  useGenerateTotpMutation,
} from '../../store/services';

import ROUTES from '../../../routes';

const useEmailFunctions = () => {
  const [forgotPasswordVisible, setForgotPasswordVisible] = useState(false);
  const [globalError, setGlobalError] = useState('');

  const { requestInProgress, loginValidation, nextAction, mfaToken } =
    useAppSelector((state) => state.auth);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: ajvResolver(loginValidation as ValidatorsProps),
  });

  const navigate = useNavigate();
  const [login] = useLoginMutation();
  const [generateTotp, { isLoading: generateTotpInProgress, isSuccess }] =
    useGenerateTotpMutation();

  const onSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    const userId = localStorage.getItem('userId');

    if (userId) {
      localStorage.removeItem('userId');
    }

    const response = (await login(values)) as {
      error: { data: { message: string }; status?: number };
    };

    if (response?.error?.data) {
      if (response?.error?.status === 403) {
        setGlobalError('Access Denied');
      } else {
        setGlobalError(response.error.data.message);
      }
    } else {
      setGlobalError('');
    }
  });

  const onGenerateTotp = useCallback(async () => {
    const response = (await generateTotp({
      mfaToken,
    })) as {
      error: { data: { message: string }; status?: number };
    };

    if (response?.error?.data) {
      if (response?.error?.status === 403) {
        setGlobalError('Access Denied');
      } else {
        setGlobalError(response.error.data.message);
      }
    } else {
      setGlobalError('');
    }
  }, [mfaToken]);

  useEffect(() => {
    if (isSuccess) {
      navigate(ROUTES.LOGIN_SETUP_TWO_FACTOR);
    }
  }, [isSuccess]);

  useEffect(() => {
    if (nextAction === AuthNextAction.SETUP_TOTP) {
      onGenerateTotp();
    } else if (nextAction === AuthNextAction.VALIDATE_TOTP) {
      navigate(ROUTES.LOGIN_VERIFY_TWO_FACTOR);
    }
  }, [nextAction]);

  return {
    globalError,
    control,
    onSubmit,
    setForgotPasswordVisible,
    requestInProgress: requestInProgress || generateTotpInProgress,
    forgotPasswordVisible,
    errors,
  };
};

export default useEmailFunctions;
